import { useQuery } from "@tanstack/react-query";
import {
  personalDetailsApi,
  addressApi,
  contactApi,
  employmentHistoryApi,
  educationApi,
  governmentDocumentApi,
} from "../employeeProfileApi";
import { queryKeys } from "./queryKeys";
import useInvalidatingMutation from "./useInvalidatingMutation";

const K = queryKeys.profile;

// A 404 here just means the section hasn't been filled in yet.
const sectionQuery = (queryKey, queryFn, employeeId) => ({
  queryKey,
  queryFn,
  enabled: !!employeeId,
  retry: false,
});

/* ---------- Personal details ---------- */
export const usePersonalDetails = (employeeId) =>
  useQuery(
    sectionQuery(
      K.personal(employeeId),
      () => personalDetailsApi.getByEmployee(employeeId),
      employeeId,
    ),
  );

export const useSavePersonalDetails = (employeeId) =>
  useInvalidatingMutation(
    ({ id, data }) =>
      id
        ? personalDetailsApi.update(id, data)
        : personalDetailsApi.create(data),
    [K.personal(employeeId)],
  );

/* ---------- Addresses ---------- */
export const useCurrentAddress = (employeeId) =>
  useQuery(
    sectionQuery(
      K.addressCurrent(employeeId),
      () => addressApi.getCurrent(employeeId),
      employeeId,
    ),
  );

export const usePermanentAddress = (employeeId) =>
  useQuery(
    sectionQuery(
      K.addressPermanent(employeeId),
      () => addressApi.getPermanent(employeeId),
      employeeId,
    ),
  );

const addressKeys = (employeeId) => [
  K.addressCurrent(employeeId),
  K.addressPermanent(employeeId),
];

/** Create or update one address: { id?, data } */
export const useSaveAddress = (employeeId) =>
  useInvalidatingMutation(
    ({ id, data }) =>
      id ? addressApi.update(id, data) : addressApi.create(data),
    addressKeys(employeeId),
  );

export const useCopyCurrentToPermanent = (employeeId) =>
  useInvalidatingMutation(
    () => addressApi.copyCurrentToPermanent(employeeId),
    addressKeys(employeeId),
  );

/* ---------- Contacts ---------- */
export const useContacts = (employeeId) =>
  useQuery(
    sectionQuery(
      K.contacts(employeeId),
      () => contactApi.getByEmployee(employeeId),
      employeeId,
    ),
  );

export const useCreateContact = (employeeId) =>
  useInvalidatingMutation(
    (contact) => contactApi.create(employeeId, contact),
    [K.contacts(employeeId)],
  );

export const useUpdateContact = (employeeId) =>
  useInvalidatingMutation(
    ({ id, contact }) => contactApi.update(id, contact),
    [K.contacts(employeeId)],
  );

export const useDeleteContact = (employeeId) =>
  useInvalidatingMutation(
    (id) => contactApi.remove(id),
    [K.contacts(employeeId)],
  );

/* ---------- Employment history ---------- */
export const useEmploymentHistory = (employeeId) =>
  useQuery(
    sectionQuery(
      K.history(employeeId),
      () => employmentHistoryApi.getByEmployee(employeeId),
      employeeId,
    ),
  );

export const useSaveEmploymentHistory = (employeeId) =>
  useInvalidatingMutation(
    ({ id, data }) =>
      id
        ? employmentHistoryApi.update(id, data)
        : employmentHistoryApi.create(data),
    [K.history(employeeId)],
  );

export const useDeleteEmploymentHistory = (employeeId) =>
  useInvalidatingMutation(
    (id) => employmentHistoryApi.remove(id),
    [K.history(employeeId)],
  );

/* ---------- Education ---------- */
export const useEducation = (employeeId) =>
  useQuery(
    sectionQuery(
      K.education(employeeId),
      () => educationApi.getByEmployee(employeeId),
      employeeId,
    ),
  );

export const useSaveEducation = (employeeId) =>
  useInvalidatingMutation(
    ({ id, data }) =>
      id ? educationApi.update(id, data) : educationApi.create(data),
    [K.education(employeeId)],
  );

export const useUploadEducationFile = (employeeId) =>
  useInvalidatingMutation(
    ({ id, file, companyId }) => educationApi.uploadFile(id, file, companyId),
    [K.education(employeeId)],
  );

export const useDeleteEducation = (employeeId) =>
  useInvalidatingMutation(
    (id) => educationApi.remove(id),
    [K.education(employeeId)],
  );

/* ---------- Government documents ---------- */
export const useGovernmentDocuments = (employeeId) =>
  useQuery(
    sectionQuery(
      K.documents(employeeId),
      () => governmentDocumentApi.getByEmployee(employeeId),
      employeeId,
    ),
  );

export const useSaveDocument = (employeeId) =>
  useInvalidatingMutation(
    ({ id, data }) =>
      id
        ? governmentDocumentApi.update(id, data)
        : governmentDocumentApi.create(employeeId, data),
    [K.documents(employeeId)],
  );

export const useUploadDocumentFile = (employeeId) =>
  useInvalidatingMutation(
    ({ id, file, companyId }) =>
      governmentDocumentApi.uploadFile(id, file, companyId),
    [K.documents(employeeId)],
  );

export const useDeleteDocument = (employeeId) =>
  useInvalidatingMutation(
    (id) => governmentDocumentApi.remove(id),
    [K.documents(employeeId)],
  );

export const useValidateDocument = (employeeId) =>
  useInvalidatingMutation(
    (docId) => governmentDocumentApi.validate(docId),
    [K.documents(employeeId)],
  );
